import {
  Box,
  Flex,
  Text,
  Button,
  useToast,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertDescription,
  Skeleton,
  Checkbox,
} from '@chakra-ui/react'
import { FC, useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import { TextareaField } from 'components/atoms'
import { DatePicker } from 'components/fromAdvisor/elements/Datepicker'
import { ModalConfirm } from 'components/molecules'
import { cacheManagementServices } from 'services'
import { AxiosError } from 'axios'
import moment from 'moment'

interface PreviewResult {
  records_to_clear?: number
}

/**
 * Summary Cache Tab Component
 */
export const SummaryCacheTab: FC = () => {
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const formik = useFormik({
    initialValues: {
      before_date: undefined as Date | undefined,
      news_ids: '',
      filter_top_400_companies: false,
    },
    onSubmit: async () => {
      await handleRefresh()
    },
  })

  const { values, setFieldValue } = formik

  const handlePreview = async () => {
    try {
      setPreviewLoading(true)
      setPreviewResult(null)

      const params: {
        before_date?: string
        news_ids?: string
        filter_top_400_companies?: boolean
      } = {}

      if (values.before_date) {
        params.before_date = moment(values.before_date).format('YYYY-MM-DD')
      }

      if (values.news_ids.trim()) {
        params.news_ids = values.news_ids.trim()
      }

      if (values.filter_top_400_companies) {
        params.filter_top_400_companies = true
      }

      const response = await cacheManagementServices.previewSummaryCache(params)
      if (response.data) {
        setPreviewResult(response.data)
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'エラー',
          description: 'プレビューの取得に失敗しました',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      const body: {
        before_date?: string
        news_ids?: number[]
        filter_top_400_companies?: boolean
      } = {}

      if (values.before_date) {
        body.before_date = moment(values.before_date).format('YYYY-MM-DD')
      }

      if (values.news_ids.trim()) {
        const ids = values.news_ids
          .split(',')
          .map(id => parseInt(id.trim()))
          .filter(id => !isNaN(id))
        if (ids.length > 0) {
          body.news_ids = ids
        }
      }

      if (values.filter_top_400_companies) {
        body.filter_top_400_companies = true
      }

      const response = await cacheManagementServices.refreshSummaryCache(body)
      if (response.data) {
        toast({
          title: '成功',
          description: `タスクが作成されました。Task ID: ${response.data.task_id}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        })
        setPreviewResult(null)
        formik.resetForm()
        onClose()
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast({
          title: 'エラー',
          description: 'キャッシュの更新に失敗しました',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      }
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleConfirmRefresh = () => {
    onOpen()
  }

  return (
    <FormikProvider value={formik}>
      <Box>
        {/* Filters Section */}
        <Box mb="24px">
          <Text fontSize="16px" fontWeight="600" mb="16px">
            フィルター
          </Text>
          <Flex flexWrap="wrap" gap="16px">
            <Box flex="1" minW="200px">
              <Text fontSize="14px" fontWeight="500" mb="8px">
                Before Date
              </Text>
              <DatePicker
                clear
                isInvalid={false}
                placeholder="日付を選択..."
                value={values.before_date}
                onChange={value => setFieldValue('before_date', value)}
              />
              <Text color="black.100" fontSize="12px" mt="4px" opacity={0.6}>
                この日付より前に更新されたキャッシュのみを更新します（空欄の場合はキャッシュがないもののみ）
              </Text>
            </Box>

            <Box flex="1" minW="200px">
              <Text fontSize="14px" fontWeight="500" mb="8px">
                News IDs
              </Text>
              <TextareaField
                height="80px"
                name="news_ids"
                placeholder="カンマ区切りでNews IDを入力 (例: 123,456,789)"
                value={values.news_ids}
                onChange={e => setFieldValue('news_ids', e.target.value)}
              />
              <Text color="black.100" fontSize="12px" mt="4px" opacity={0.6}>
                特定のNews IDを指定します（空欄の場合はすべて）
              </Text>
            </Box>
          </Flex>
          <Box mt="16px">
            <Checkbox
              isChecked={values.filter_top_400_companies}
              onChange={e =>
                setFieldValue('filter_top_400_companies', e.target.checked)
              }
            >
              <Text fontSize="14px" fontWeight="500">
                過去5年間のトップ400社のみ
              </Text>
            </Checkbox>
            <Text color="black.100" fontSize="12px" mt="4px" opacity={0.6}>
              トップ400社のフィルターを5年間適用します
            </Text>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Flex gap="16px" mb="24px">
          <Button
            isLoading={previewLoading}
            size="md"
            variant="outline"
            onClick={handlePreview}
          >
            プレビュー
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!previewResult || isRefreshing}
            isLoading={isRefreshing}
            size="md"
            onClick={handleConfirmRefresh}
          >
            🔄 キャッシュをクリアして更新
          </Button>
        </Flex>

        {/* Preview Results */}
        {previewLoading ? (
          <Skeleton borderRadius="12px" height="100px" />
        ) : previewResult ? (
          <Alert borderRadius="12px" mb="16px" status="info">
            <AlertIcon />
            <Box flex="1">
              <AlertDescription>
                <Text fontWeight="600" mb="8px">
                  📊 更新対象レコード:{' '}
                  {previewResult.records_to_clear?.toLocaleString() || 0}
                </Text>
                <Text fontSize="14px">⚠️ この操作により:</Text>
                <Text fontSize="14px" ml="16px">
                  • これらのレコードの既存キャッシュがクリアされます
                </Text>
                <Text fontSize="14px" ml="16px">
                  • バックグラウンドで新しいキャッシュが生成されます
                </Text>
                <Text fontSize="14px" ml="16px">
                  • 完了まで数分かかる場合があります
                </Text>
              </AlertDescription>
            </Box>
          </Alert>
        ) : null}

        <ModalConfirm
          isKeepAll
          isOpen={isOpen}
          textCancel="キャンセル"
          textOK="確認"
          title={`${
            previewResult?.records_to_clear || 0
          }件のキャッシュを更新しますか？\n元に戻せません。`}
          onClose={onClose}
          onSubmit={handleRefresh}
        />
      </Box>
    </FormikProvider>
  )
}
